import { useMemo } from 'react';
import { useQuery } from 'react-query';
import moment from 'moment';

import HouseholdAffordableRecertDueDateService from '../../../../services/householdAffordableRecertDueDateService';
import { LIHTC_HOME_PROGRAM_NAME } from '../../../../constants/affordableProgramNames';

const parseRecertDueDateInitialValues = (
  householdRecertDueDates: Array<Object>,
) => {
  return householdRecertDueDates.reduce((recertDueDates, dueDate) => {
    const programName = dueDate?.pap?.map?.name ?? null;
    if (!programName) {
      return recertDueDates;
    }
    const recertDueDate = dueDate?.recertDueDate ?? null;
    const propertyAffordableProgramId = dueDate?.pap?.id ?? null;
    return {
      ...recertDueDates,
      [programName]: {
        recertDueDate: recertDueDate ? moment(recertDueDate) : null,
        propertyAffordableProgramId,
      },
    };
  }, {});
};

export const useFetchRecertDueDates = ({
  organizationId,
  propertyId,
  householdId,
  floorPlanAffordablePrograms,
  selectedProperty,
  enabled = true,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
  floorPlanAffordablePrograms: Array<Object>,
  selectedProperty: Object,
  enabled?: boolean,
}) => {
  const pap = selectedProperty?.pap ?? [];
  const layeredPrograms = pap.filter((pap) =>
    pap?.masterAffordableProgram?.name.includes('/'),
  );

  const householdRecertDueDateService = useMemo(() => {
    return new HouseholdAffordableRecertDueDateService();
  }, []);

  const query = useQuery(
    ['fetchRecertDueDates', organizationId, propertyId, householdId],
    async () => {
      const abortController = new AbortController();
      const options = { signal: abortController.signal };
      const recertDueDatesResponse =
        await householdRecertDueDateService.getHouseholdAffordableRecertDueDates(
          organizationId,
          propertyId,
          householdId,
          options,
        );

      let recertDueDates = recertDueDatesResponse;

      const fpHasLayeredPrograms = floorPlanAffordablePrograms?.some((fpap) =>
        fpap?.propertyAffordableProgram?.masterAffordableProgram?.name?.includes(
          '/',
        ),
      );

      if (fpHasLayeredPrograms) {
        const layeredProgramsNames = [
          ...new Set(
            layeredPrograms
              .map((program) =>
                program.masterAffordableProgram.name?.split('/'),
              )
              .flat(Infinity),
          ),
        ];

        recertDueDates = recertDueDatesResponse.filter(
          ({ pap }) =>
            pap?.map?.name && !layeredProgramsNames.includes(pap.map.name),
        );

        // LIHTC/HOME specific case
        const lihtcHome = layeredPrograms.find(
          ({ pap }) => pap?.map?.name === LIHTC_HOME_PROGRAM_NAME,
        );
        if (lihtcHome != null) {
          const fpProgramsNames = [
            ...new Set(
              floorPlanAffordablePrograms
                .map((program) =>
                  program.propertyAffordableProgram.masterAffordableProgram.name?.split(
                    '/',
                  ),
                )
                .flat(Infinity),
            ),
          ];
          const shouldApplyLIHTCHOMERule =
            JSON.stringify(LIHTC_HOME_PROGRAM_NAME.split('/').sort()) ===
            JSON.stringify(fpProgramsNames.sort());
          if (shouldApplyLIHTCHOMERule) {
            recertDueDates = recertDueDatesResponse.filter(
              ({ pap }) => pap?.map?.name === LIHTC_HOME_PROGRAM_NAME,
            );
          }
        }
      }

      const parsedInitialValues =
        parseRecertDueDateInitialValues(recertDueDates);

      return parsedInitialValues;
    },
    {
      enabled,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
